import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiRequest, apiFormRequest, shortDate } from '../../utils';
import './MemberHome.css';

/* The company's own blogs — the two ways it puts something in front of its own people:

     1. ARTICLES it writes itself. They land published, not proposed: a company writes for its
        own people in its own space, so there is nobody to review it. Part of the
        employee-engagement product, so the form only appears for a company that bought it —
        the articles themselves are always listed, because taking your own content down must
        never depend on a membership still being active.
     2. FEEDS it mixes in, which any Bravos company may do.

   Whatever either produces gets mixed into the articles the company's people read on their
   home — one stream, ours and theirs.

   Two things this screen has to be honest about, and both are on it:
     - we do not vet what a customer's feed publishes, so every item from one is labelled and the
       disclaimer says so in plain words;
     - a feed we cannot read shows "0 items" rather than an error, because the parser is
       fail-safe by design and silence would otherwise be indistinguishable from an empty blog.

   The route is still /company-feeds: that is what live links point at, and a screen gaining a
   section is no reason to break them. */

const CompanyBlogs = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  // The article being written. Only the title and the link are required — an article nobody
  // can open is not an article.
  const [title, setTitle] = useState('');
  const [link, setLink] = useState('');
  const [summary, setSummary] = useState('');
  const [file, setFile] = useState(null);
  const [postNotice, setPostNotice] = useState(null);
  const fileInput = React.useRef(null);

  const load = useCallback(() => {
    apiRequest('GET', '/bravos/company-feeds')
      .then(setData)
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const addPost = (e) => {
    e.preventDefault();
    if (busy || !title.trim() || !link.trim()) return;
    setBusy(true);
    setPostNotice(null);
    // apiRequest always sends JSON, so a picture needs the form variant. Same fields either
    // way — the picture is the only reason the request changes shape.
    const fields = { title: title.trim(), url: link.trim(), summary: summary.trim() };
    let send;
    if (file) {
      const form = new FormData();
      Object.entries(fields).forEach(([k, v]) => { if (v) form.append(k, v); });
      form.append('file', file);
      send = apiFormRequest('/bravos/company-posts', form);
    } else {
      send = apiRequest('POST', '/bravos/company-posts', fields);
    }
    send
      .then((res) => {
        setTitle(''); setLink(''); setSummary(''); setFile(null);
        if (fileInput.current) fileInput.current.value = '';
        setPostNotice(t('articlePublished'));
        // The response carries the whole list back, so the screen updates without asking the
        // feeds for their item counts again.
        setData((d) => ({ ...d, posts: (res && res.posts) || (d && d.posts) || [] }));
      })
      .catch((err) => setPostNotice(t(err.message, { defaultValue: err.message })))
      .finally(() => setBusy(false));
  };

  const removePost = (post) => {
    // eslint-disable-next-line no-alert
    if (busy || !window.confirm(t('deleteArticleConfirm'))) return;
    setBusy(true);
    apiRequest('DELETE', `/bravos/company-posts/${post.id}`)
      .then((res) => setData((d) => ({ ...d, posts: (res && res.posts) || [] })))
      .catch((err) => setPostNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  const addFeed = () => {
    if (busy || !url.trim()) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', '/bravos/company-feeds', { url: url.trim(), label: label.trim() })
      .then((res) => {
        setUrl('');
        setLabel('');
        // The feed is added either way; a warning only means we read it and it held nothing —
        // almost always the wrong address rather than a blog with nothing on it.
        if (res && res.warning) setNotice(t(res.warning));
        load();
      })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  const removeFeed = (feed) => {
    // eslint-disable-next-line no-alert
    if (busy || !window.confirm(t('deleteFeedConfirm'))) return;
    setBusy(true);
    apiRequest('DELETE', `/bravos/company-feeds/${feed.id}`)
      .then(load)
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  const feeds = (data && data.feeds) || [];
  const posts = (data && data.posts) || [];
  const full = data && feeds.length >= data.max;

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/journey" />
        <Title my="1" className="mt-3">
          <Icon name="newspaper" theme="primary" className="me-2" />
          {t('ourBlogs')}
        </Title>

        {data === null ? (
          <span className="auth__spinner" />
        ) : (
          <>
            <p className="bravos-q__hint mb-2">{t('ourBlogsNote')}</p>

            {posts.map((p) => (
              <div key={p.id} className="bravos-member__alloc">
                <span className="bravos-member__alloc-name">
                  <a href={p.url} target="_blank" rel="noopener noreferrer">{p.title}</a>
                  {p.date && (
                    <span className="bravos-member__alloc-type">
                      {' · '}{shortDate(p.date, i18n.language)}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="bravos-member__remove"
                  onClick={() => removePost(p)}
                  disabled={busy}
                  title={t('delete')}
                >
                  <Icon name="trash" />
                </button>
              </div>
            ))}
            {posts.length === 0 && <p className="text-muted">{t('noArticlesYet')}</p>}
            {postNotice && <p className="bravos-cal__notice">{postNotice}</p>}

            {/* Writing one is the paid half. A company without the engagement product still
                sees what it published and can take it down — it just cannot add more. */}
            {data.can_write_posts ? (
              <form onSubmit={addPost} className="mt-3">
                <label className="bravos-form__label">{t('articleTitle')}</label>
                <input className="bravos-form__input" type="text" value={title} maxLength={200}
                       onChange={(e) => setTitle(e.target.value)} required />

                <label className="bravos-form__label">{t('articleLink')}</label>
                <input className="bravos-form__input" type="url" value={link}
                       placeholder="https://…" onChange={(e) => setLink(e.target.value)}
                       required />

                <label className="bravos-form__label">{t('articleSummary')}</label>
                <textarea className="bravos-form__input" rows={3} value={summary} maxLength={400}
                          onChange={(e) => setSummary(e.target.value)} />

                <label className="bravos-form__label">
                  {t('articleImage')}
                  <input ref={fileInput} type="file" accept="image/*"
                         onChange={(e) => setFile(e.target.files[0] || null)} />
                </label>
                <p className="bravos-q__hint">{t('articleImageHelp')}</p>

                <button type="submit" className="bravos-event__claim mt-2"
                        disabled={busy || !title.trim() || !link.trim()}>
                  {busy ? t('pleaseWait') : t('publishArticle')}
                </button>
              </form>
            ) : (
              <p className="bravos-q__hint mt-2">{t('articlesNeedEngagement')}</p>
            )}
          </>
        )}
      </Card>

      {data && (
        <Card>
          <Title my="1" className="mt-2">
            <Icon name="rss" theme="primary" className="me-2" />
            {t('ourFeeds')}
          </Title>
          <p className="bravos-q__hint mb-2">{t('ourFeedsNote', { max: data.max })}</p>
          {notice && <p className="bravos-cal__notice">{notice}</p>}

          {feeds.map((f) => (
            <div key={f.id} className="bravos-member__alloc">
              <span className="bravos-member__alloc-name">
                {f.label || f.url}
                <span className="bravos-member__alloc-type">
                  {' · '}{t('feedItemCount', { n: f.item_count })}
                </span>
              </span>
              <button
                type="button"
                className="bravos-member__remove"
                onClick={() => removeFeed(f)}
                disabled={busy}
                title={t('delete')}
              >
                <Icon name="trash" />
              </button>
            </div>
          ))}
          {feeds.length === 0 && <p className="text-muted">{t('noFeedsYet')}</p>}

          {!full && (
            <div className="bravos-form">
              <input
                type="url"
                value={url}
                placeholder={t('feedUrlPlaceholder')}
                onChange={(e) => setUrl(e.target.value)}
              />
              <input
                type="text"
                value={label}
                maxLength={60}
                placeholder={t('feedLabelPlaceholder')}
                onChange={(e) => setLabel(e.target.value)}
              />
              <button
                type="button"
                className="bravos-submit"
                onClick={addFeed}
                disabled={busy || !url.trim()}
              >
                {busy ? t('pleaseWait') : t('addFeed')}
              </button>
            </div>
          )}

          <p className="bravos-feed__disclaimer">{t('feedDisclaimer')}</p>
        </Card>
      )}
    </Base>
  );
};

export default CompanyBlogs;
